import { CalendarIcon } from '@heroicons/react/24/outline';
import { default as Divider } from 'antd/es/divider';
import type { ReactNode } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { formatDate } from 'utils/dayjs';

import type { AuthScheme } from 'api';

import { CopyIcon } from '@atoms/Icon/CopyIcon';

import { AuthenticationStatusBadge } from './AuthenticationStatusBadge';
import { AuthenticationCardActions } from './CardActions';
import {
  AUTHENTICATION_CARD_COPY_UUID_TEST_ID,
  AUTHENTICATION_CARD_TEST_ID
} from './constants';

type AuthenticationCardProps = {
  authenticationKey: AuthScheme;
};

export const AuthenticationCard = ({
  authenticationKey: k
}: AuthenticationCardProps) => {
  const openNotification = useOpenNotification();

  const onCopySuccess = (label: string) =>
    openNotification({
      kind: 'success',
      message: `${label} copied to your clipboard`
    });

  return (
    <div
      data-testid={AUTHENTICATION_CARD_TEST_ID(k.uuid)}
      className="flex w-[363px] max-w-full flex-col gap-3 rounded-lg border border-border-default bg-white p-4"
    >
      <div className="flex items-center gap-4 pb-4">
        <p className="flex-1 text-base font-medium leading-[22px] text-content-primary">
          {k.name}
        </p>
        <AuthenticationStatusBadge enabled={k.enabled} />
        <AuthenticationCardActions authenticationKey={k} />
      </div>

      <div className="flex flex-col gap-2 p-1.5">
        <p className="text-xs font-semibold leading-[14px] text-content-primary">
          X-Auth-UUID
        </p>
        <div className="flex items-center justify-between rounded-lg bg-surface-muted p-2">
          <span className="font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary">
            {k.uuid}
          </span>
          <CopyToClipboard
            text={k.uuid}
            onCopy={() => onCopySuccess('X-Auth-UUID')}
          >
            <button
              type="button"
              data-testid={AUTHENTICATION_CARD_COPY_UUID_TEST_ID(k.uuid)}
              aria-label="Copy X-Auth-UUID"
              className="cursor-pointer text-content-secondary"
            >
              <CopyIcon
                width={20}
                height={20}
                stroke="auto"
                className="shrink-0"
              />
            </button>
          </CopyToClipboard>
        </div>
      </div>

      {k.secret_preview && (
        <div className="flex flex-col gap-2 p-1.5">
          <p className="text-xs font-semibold leading-[14px] text-content-primary">
            Secret
          </p>
          <div className="flex items-center rounded-lg bg-surface-muted p-2">
            <span className="flex-1 font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary">
              {k.secret_preview}
            </span>
          </div>
        </div>
      )}

      <Divider className="m-0 mt-auto" />

      <div className="flex w-full flex-col gap-1.5">
        <DetailRow
          icon={<CalendarIcon className="size-[14px]" />}
          label="Created"
          value={formatDate(k.created_at, 'utc')}
        />
      </div>
    </div>
  );
};

type DetailRowProps = {
  icon: ReactNode;
  label: string;
  value: string;
};

const DetailRow = ({ icon, label, value }: DetailRowProps) => (
  <div className="flex items-center justify-between p-1.5">
    <div className="flex flex-1 items-center gap-1.5">
      <span className="flex size-5 items-center justify-center rounded bg-surface-muted p-[2.5px] text-content-secondary">
        {icon}
      </span>
      <span className="text-xs leading-[14px] text-content-secondary">
        {label}
      </span>
    </div>
    <span className="text-right text-xs font-semibold leading-[14px] text-content-primary">
      {value}
    </span>
  </div>
);
